/**
 * Turn-time labels for the in-game player cards and profile stats. The API
 * keeps running totals per seat and per player (apps/api/src/lib/turnTimes.ts)
 * and sends the averages; the current turn's clock comes from turnStartedAt.
 */
import { formatDuration, type GameEvent, type VacationResponse } from '@warcarp/api-types'
import { isVacationActive } from './vacation'

/** Compact average for the in-game player card: "12m", or "—" before any completed turn. */
export function formatAverageTurn(averageTurnSeconds: number | null | undefined): string {
  if (averageTurnSeconds === null || averageTurnSeconds === undefined) return '—'
  return formatDuration(averageTurnSeconds * 1000)
}

function unit(n: number, name: string): string {
  return `${n} ${name}${n === 1 ? '' : 's'}`
}

/**
 * Spelled-out duration in one unit, rounded to the nearest: "45 seconds",
 * "12 minutes", "5 hours", "3 days". A value that rounds up to the next unit
 * moves to it (59.6 minutes is "1 hour", not "60 minutes").
 */
export function formatDurationLong(seconds: number): string {
  const s = Math.max(0, Math.round(seconds))
  if (s < 60) return unit(s, 'second')
  const minutes = Math.round(s / 60)
  if (minutes < 60) return unit(minutes, 'minute')
  const hours = Math.round(s / 3600)
  if (hours < 24) return unit(hours, 'hour')
  return unit(Math.round(s / 86400), 'day')
}

/** Average for the stats card, spelled out, or "—" before any completed turn. */
export function formatAverageTurnValue(averageTurnSeconds: number | null | undefined): string {
  return averageTurnSeconds === null || averageTurnSeconds === undefined ? '—' : formatDurationLong(averageTurnSeconds)
}

/** Home tiles show elapsed turn time, never the age of an unrelated mutation. */
export function turnWaitingLabel(turnStartedAt: string | null | undefined, now = Date.now()): string | null {
  if (!turnStartedAt) return null
  const started = Date.parse(turnStartedAt)
  if (!Number.isFinite(started)) return null
  const seconds = Math.max(0, (now - started) / 1000)
  return seconds < 60 ? 'less than a minute' : formatDurationLong(seconds)
}


/**
 * How long the current turn has run, or null when the clock isn't known. Shows
 * seconds for the first hour ("45s", "11m 32s") so the live clock visibly
 * ticks, then the coarser "3h 12m" / "2d 4h".
 */
export function turnClockLabel(turnStartedAt: string | null | undefined, now: number = Date.now()): string | null {
  if (!turnStartedAt) return null
  const started = Date.parse(turnStartedAt)
  if (!Number.isFinite(started)) return null
  const seconds = Math.max(0, Math.floor((now - started) / 1000))
  if (seconds >= 60 && seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`
  return formatDuration(seconds * 1000)
}

/** A vacation suspends the current turn clock until its effective start resets. */
export function visibleTurnClockLabel(turnStartedAt: string | null | undefined, vacation: VacationResponse | null | undefined, now: number): string | null {
  if (isVacationActive(vacation, now)) return null
  const endedAt = vacation ? Date.parse(vacation.endsAt) : NaN
  const startedAt = turnStartedAt ? Date.parse(turnStartedAt) : NaN
  // A still-cached vacation may expire between API refreshes. Resume locally
  // from its end instead of briefly showing the original multi-day turn age.
  const effectiveStart = Number.isFinite(endedAt) && endedAt <= now && (!Number.isFinite(startedAt) || endedAt > startedAt)
    ? vacation!.endsAt : turnStartedAt
  return turnClockLabel(effectiveStart, now)
}

/**
 * When the turn in progress began. Read from the newest turn-start event in
 * the state itself, so the clock resets to 0 in the same update that passes the
 * turn (bot steps stream state patches that carry no Game.turnStartedAt).
 * Falls back to the server's turnStartedAt when the tail has no turn-start for
 * the current player (claim phase, AI takeover, trimmed tail).
 */
export function currentTurnStartedAt(
  state: { players: readonly string[]; turnIndex: number; events?: readonly GameEvent[] | null } | null | undefined,
  fallback: string | null | undefined,
): string | null {
  const events = state?.events ?? []
  for (let i = events.length - 1; i >= 0; i--) {
    const event = events[i]!
    if (event.kind !== 'turn-start') continue
    return event.player === state!.players[state!.turnIndex] ? new Date(event.ts).toISOString() : (fallback ?? null)
  }
  return fallback ?? null
}

